<template>
  <div role="textbox" :style="{width}" :aria-multiline="type === 'textarea'" :class="['relative', 'transition-all', 'duration-250', baseCss, focus || touched ? `${baseCss}-focus` : null, disabled ? 'disabled' : null, 'block']">
    <label :class="[color, 'block']">
      <text-field-label v-if="label" :text="label" :required="required" :color="labelColor" :color-lightness="labelColorLightness" />
      <table-layout tag="label" y-align="middle" equal-cells>
        <row tag="span">
          <cell tag="span" x-align="center" v-if="iconLeft" :class="[`${baseCss}-icon-wrapper`, 'py-2']">
            <icon @click="$emit('left-icon-click')" :provider="iconProvider" :name="iconLeft" :color="focus || touched ? color : 'black'" :color-lightness="focus || touched ? colorLightness : null" />
          </cell>
          <cell tag="span" :class="[`${baseCss}-input-wrapper`, 'p-2']" full-width>
            <input
              v-if="!multiline"
              ref="input"
              @focus="focus = true;$emit('focus')"
              @blur="focus = false;$emit('blur')"
              @input="$emit('input', $event.target.value)"
              @click="$emit('click')"
              @keyup="$emit('keyup', $event)"
              @keydown="$emit('keydown', $event)"
              :readonly="readonly"
              :disabled="disabled"
              :placeholder="placeholder"
              :value="value"
              :type="type"
              :id="id"
              :name="name"
              :class="['pa-2', 'bg-transparent', 'block', 'w-full', 'text-sm', focus || touched ? internalTextColor : $dark ? 'text-white' : 'text-black']"
              :spellcheck="spellcheck"
              :maxlength="maxLength"
              :autofocus="autofocus"
              :autocomplete="autocomplete" />
            <textarea
              v-else
              ref="input"
              @focus="focus = true;$emit('focus')"
              @blur="focus = false;$emit('blur')"
              @input="$emit('input', $event.target.value)"
              @click="$emit('click')"
              @keyup="$emit('keyup')"
              @keydown="$emit('keydown')"
              :readonly="readonly"
              :disabled="disabled"
              :placeholder="placeholder"
              :value="value"
              :id="id"
              :name="name"
              :class="['pa-2', 'bg-transparent', 'block', 'w-full', 'resize-none', 'text-sm', focus || touched ? internalTextColor : 'text-black']"
              :rows="rows"
              :cols="cols"
              :spellcheck="spellcheck"
              :maxlength="maxLength"
              :autofocus="autofocus"
              :autocomplete="autocomplete" />
          </cell>
          <cell tag="span" x-align="center" v-if="reveal && focus && type === 'password'" :class="[`${baseCss}-icon-wrapper`, 'py-2']">
            <icon :provider="iconProvider" :color="focus ? color : 'black'" :color-lightness="focus || touched ? colorLightness : null" name="eye" @mousedown="toggleType('text')" @mouseup="toggleType('password')" :class="['cursor-pointer']" />
          </cell>
          <cell tag="span" x-align="center" v-if="clearable && focus" :class="[`${baseCss}-icon-wrapper`, 'py-2']">
            <icon :provider="iconProvider" :color="focus ? color : 'black'" :color-lightness="focus || touched ? colorLightness : null" name="close" @mousedown="reset" :class="['cursor-pointer']" />
          </cell>
          <cell tag="span" x-align="center" v-if="iconRight" :class="[`${baseCss}-icon-wrapper`, 'py-2']">
            <icon @click="$emit('right-icon-click')" :provider="iconProvider" :color="focus || touched ? color : 'black'" :color-lightness="focus || touched ? colorLightness : null" :name="iconRight" />
          </cell>
        </row>
      </table-layout>
      <line-ripple :visible="focus || touched" :bg-color="rippleColor" :bg-color-lightness="rippleColorLightness" :ripple-bg-color="color" :ripple-bg-color-lightness="colorLightness" />
      <text-field-label :color="hintColor" :color-lightness="hintColorLightness" v-if="hint" :text="hint" align="right" />
      <text-field-label :color="errorColor" :color-lightness="errorColorLightness" v-if="error" :text="error" align="right" />
    </label>
  </div>
</template>
<script>
import methods from '../../mixins/methods'
import props from '../../mixins/props'
import computed from '../../mixins/computed'
import TextFieldLabel from '../text-field-label/text-field-label'

export default {
  name: 'TextField',
  components: {
    TextFieldLabel
  },
  mixins: [
    props,
    methods,
    computed
  ],
  data () {
    return {
      baseCss: this.componentBaseCss('text-field'),
      focus: false
    }
  },
  computed: {
    touched () {
      return !!this.value
    }
  },
  props: {
    iconProvider: {
      type: String,
      default: 'mdi'
    },
    rippleColor: {
      type: String
    },
    rippleColorLightness: {
      type: Number
    },
    hintColor: {
      type: String
    },
    hintColorLightness: {
      type: [Number, String]
    },
    errorColor: {
      type: String,
      default: 'red'
    },
    errorColorLightness: {
      type: [Number, String],
      default: 500
    },
    rows: {
      type: [
        Number,
        String
      ],
      default: 5
    },
    cols: {
      type: [
        Number,
        String
      ],
      default: 5
    },
    clearable: {
      type: Boolean
    },
    width: {
      type: String
    },
    autofocus: {
      type: Boolean
    },
    color: {
      type: String,
      default: 'black'
    },
    multiline: {
      type: Boolean
    },
    readonly: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    reveal: {
      type: Boolean
    },
    placeholder: {
      type: String
    },
    label: {
      type: String
    },
    hint: {
      type: String
    },
    error: {
      type: String
    },
    maxLength: {
      type: Number
    },
    value: {
      required: false
    },
    rippleBgColor: {
      type: String
    },
    rippleBgColorLightness: {
      type: Number
    },
    type: {
      type: String,
      required: false,
      default: 'text',
      validator: value => {
        return [
          'text',
          'password',
          'number'
        ].includes(value)
      }
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    spellcheck: {
      type: Boolean,
      default: false
    },
    iconLeft: {
      required: false
    },
    iconRight: {
      required: false
    },
    name: {
      type: String
    },
    id: {
      type: String
    },
    required: {
      type: Boolean,
      default: false
    },
    labelColor: {
      type: String
    },
    labelColorLightness: {
      type: [String, Number]
    }
  },
  methods: {
    reset () {
      const ref = this.$refs.input
      ref.value = ''
      this.$emit('input', ref.value)
    },
    toggleType (type) {
      this.$refs.input.setAttribute('type', type)
    }
  }
}
</script>
<style>
.bcq-text-field *
{
  margin: 0;
  border: 0;
  outline: none;
}

.bcq-text-field input
{
  line-height: 24px;
}

.bcq-text-field .bcq-text-field-icon-wrapper
{
  width: 32px;
}

.bcq-text-field:not(.bcq-text-field-focus) .bcq-table-layout
{
  filter: grayscale(100%);
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/text-field/text-field.vue